import { z } from 'zod';
import { sha256HexSchema } from '../macroPackage/canonicalJson';

export type GraphJson = null | boolean | number | string | GraphJson[] | GraphJsonObject;
export interface GraphJsonObject { [key: string]: GraphJson }

/** JSON across the sandbox boundary never contains getters, host objects or cycles. */
export class GraphJsonContract {
  public static accepts(value: unknown): value is GraphJson {
    const pending: Array<{ value: unknown; leave?: boolean }> = [{ value }];
    const ancestors = new Set<object>();
    while (pending.length) {
      const item = pending.pop()!;
      if (item.leave) { ancestors.delete(item.value as object); continue; }
      if (item.value === null || typeof item.value === 'string' || typeof item.value === 'boolean') continue;
      if (typeof item.value === 'number' && Number.isFinite(item.value)) continue;
      if (typeof item.value !== 'object' || !item.value || ancestors.has(item.value)) return false;
      const prototype = Object.getPrototypeOf(item.value);
      if (!Array.isArray(item.value) && !GraphJsonContract.plainPrototype(prototype)) return false;
      ancestors.add(item.value);
      pending.push({ value: item.value, leave: true });
      const descriptors = Object.getOwnPropertyDescriptors(item.value);
      if (Object.getOwnPropertySymbols(item.value).length) return false;
      if (Array.isArray(item.value) && Object.keys(item.value).length !== item.value.length) return false;
      if (Array.isArray(item.value) && Object.keys(item.value).some(key => !/^(0|[1-9]\d*)$/.test(key) || Number(key) >= (item.value as unknown[]).length)) return false;
      if (Object.keys(descriptors).some(key => key !== 'length' && !descriptors[key].enumerable)) return false;
      for (const key of Object.keys(item.value)) {
        if (!Object.prototype.hasOwnProperty.call(descriptors[key], 'value')) return false;
        pending.push({ value: descriptors[key].value });
      }
    }
    return true;
  }
  private static plainPrototype(prototype: object | null): boolean {
    if (prototype === null || prototype === Object.prototype) return true;
    // JSON received from another Window/Worker/Node realm has that realm's genuine Object.prototype.
    const constructor = Object.getOwnPropertyDescriptor(prototype, 'constructor');
    return Object.getPrototypeOf(prototype) === null && !!constructor && 'value' in constructor
      && typeof constructor.value === 'function'
      && Function.prototype.toString.call(constructor.value) === Function.prototype.toString.call(Object);
  }
}

export const graphJsonSchema = z.custom<GraphJson>(GraphJsonContract.accepts, 'Expected finite, acyclic plain JSON');
export const graphJsonObjectSchema = graphJsonSchema.refine((value): value is GraphJsonObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value), 'Expected a JSON object');
export const graphIdSchema = z.string().uuid();
export const graphCorrelationIdSchema = z.string().min(1).max(128);
export const graphCountSchema = z.number().int().nonnegative().safe();
export const graphDurationSchema = z.number().int().positive().safe().nullable();
export const graphOutcomeSchema = z.enum(['succeeded', 'failed', 'cancelled', 'uncertain']);
export const graphAssetPathSchema = z.string().min(1).refine(path =>
  !/[\x00-\x1f]/.test(path)
  && !path.split('/').some(part => /[. ]$/.test(part) || /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(part))
  && !path.includes('\\') && !path.includes(':') && !path.startsWith('/')
  && path.split('/').every(part => part !== '' && part !== '.' && part !== '..'), 'Expected a relative asset path');
export const graphAssetSchema = z.object({ file: graphAssetPathSchema,
  sha256: sha256HexSchema, bytes: graphCountSchema }).strict();
export type GraphAsset = z.infer<typeof graphAssetSchema>;
export const graphNodeRoleSchema = z.enum(['entry', 'decision', 'terminal', 'junction']);
export const graphFlowTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('step'), stepId: graphIdSchema }).strict(),
  z.object({ kind: z.literal('complete'), outcome: z.enum(['succeeded', 'failed']) }).strict(),
]);
export type GraphFlowTarget = z.infer<typeof graphFlowTargetSchema>;
export const graphDiagnosticSchema = z.object({ severity: z.enum(['error', 'warning']), code: z.string().min(1),
  message: z.string().min(1), definitionId: graphIdSchema.optional(), stepId: graphIdSchema.optional(),
  nodeId: graphIdSchema.optional(), moduleId: graphIdSchema.optional(), scriptId: graphIdSchema.optional(),
  attachmentId: graphCorrelationIdSchema.optional(), edgeId: graphIdSchema.optional(), junctionId: graphIdSchema.optional(),
}).strict();
export type GraphDiagnostic = z.infer<typeof graphDiagnosticSchema>;
